import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { StopCircle, User, Calendar } from 'feather-icons-react/build/IconComponents';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal, Tabs, Tab, FooterButton } from 'Core/components';
import { showToast } from 'Core/redux/toast/slice';
import CustomPropTypes from 'Core/prop-types';
import { addInspectionPlan, editInspectionPlan, extendInspectionPlan, deleteInspectionPlanByMasterId } from './store/slice';
import { AppModal } from '../components/app-modals';
import { selectStatuses } from 'Core/redux/status/selectors';
import { useTransformOptions } from 'Core/hooks';
import { fetchStatuses } from '../../core/redux/status/slice';
import SystemRequirement from './system-requirement';
import ToolsRequired from './tools-required';
import Parameter from './parameter';
import ParametersList from './parameters-list';
import InspectionSteps from './inspection-steps';
import InspectionParameters from './inspection-parameters';
import { selectParts } from 'Core/redux/part/selectors';

const initData = {
  id: 0,
  partId: 0,
  type: '',
};

const InspectionPlanAddEdit = ({ showModal, modalData, onCloseModal, onChanges }) => {
  console.log('InspectionPlanAddEdit -> modalData', modalData);

  const inspectionPlanData = modalData;
  const [formData, setFormData] = useState(inspectionPlanData ? inspectionPlanData : initData);
  const [stepsData, setStepsData] = useState(inspectionPlanData?.steps || []);
  const [safetyData, setSafetyData] = useState(inspectionPlanData?.safetyDocuments || []);
  const [toolsData, setToolsData] = useState(inspectionPlanData?.toolsDocuments || []);

  const [parameterData, setParameterData] = useState(inspectionPlanData?.parameters || []);
  const [activeParameterId, setActiveParameterId] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const resolvePositiveId = (...candidates) => {
    for (const candidate of candidates) {
      const numeric = Number(candidate);
      if (Number.isFinite(numeric) && numeric > 0) {
        return numeric;
      }
    }
    return null;
  };

  const resolvedPartId = useMemo(() => {
    const primaryPartId = resolvePositiveId(
      formData?.partId,
      inspectionPlanData?.partId,
    );
    if (primaryPartId) return primaryPartId;

    const fallbackInspectionPlanId = resolvePositiveId(
      formData?.inspectionPlanId,
      formData?.inspectionPlanMasterId,
      inspectionPlanData?.inspectionPlanId,
      inspectionPlanData?.inspectionPlanMasterId,
      inspectionPlanData?.id,
      modalData?.inspectionPlanId,
      modalData?.inspectionPlanMasterId,
      modalData?.id,
    );
    if (fallbackInspectionPlanId) return fallbackInspectionPlanId;

    return 0;
  }, [formData, inspectionPlanData, modalData]);

  const resolvedInspectionPlanId = useMemo(() => {
    const inspectionPlanId = resolvePositiveId(
      inspectionPlanData?.inspectionPlanId,
      inspectionPlanData?.inspectionPlanMasterId,
      inspectionPlanData?.id,
      formData?.inspectionPlanMasterId,
      formData?.id,
      modalData?.inspectionPlanId,
      modalData?.inspectionPlanMasterId,
      formData?.inspectionPlanId,
    );
    return inspectionPlanId || 0;
  }, [inspectionPlanData, modalData, formData]);

  const parts = useSelector(selectParts);
  const partsOptions = useTransformOptions(parts, 'id', 'partNumber', '', 'Select Part');

  console.log(partsOptions);
  const dispatch = useDispatch();
  const isEditMode = Boolean(inspectionPlanData?.id || inspectionPlanData?.inspectionPlanId);

  const onChangesInspectionPlanForm = data => {
    setFormData(data);
  };

  const onChangesSteps = data => {
    setStepsData(data);
  };

  const onChangesSafety = data => {
    setSafetyData(data);
  };
  const onChangesTools = data => {
    setToolsData(data);
  };

  const onChangesParameter = data => {
    setParameterData(data);
  };

  const handleEditParameter = useCallback(
    parameterId => {
      if (parameterId) {
        setActiveParameterId(parameterId);
      }
      setActiveTabIndex(3);
    },
    [setActiveTabIndex],
  );

  const handleParametersLoaded = useCallback(loadedParameters => {
    if (Array.isArray(loadedParameters)) {
      setParameterData(loadedParameters);
    } else {
      setParameterData([]);
    }
  }, []);

  const handleClose = () => {
    onCloseModal();
  };
  const onExtend = () => {
    // Get the current partId from formData (should be the selected part)
    const currentPartId = formData.partId || 0;
    
    // Find the selected part to get the correct partNumber
    const selectedPart = partsOptions.find(part => part.value === currentPartId);
    const partNumber = selectedPart?.label || formData.partNumber || '';
    
    const payload = {
      ...formData,
      partId: currentPartId,
      partNumber: partNumber,
      savedAt: new Date().toISOString(),
    };
    dispatch(extendInspectionPlan(payload));
  };

  const handleDelete = () => {
    // Get inspectionPlanMasterId from modalData - try different possible field names
    const inspectionPlanMasterId = inspectionPlanData?.inspectionPlanMasterId || 
                                 inspectionPlanData?.id || 
                                 inspectionPlanData?.inspectionPlanId ||
                                 formData?.id ||
                                 formData?.inspectionPlanId ||
                                 formData?.inspectionPlanMasterId;
    
    if (!inspectionPlanMasterId || inspectionPlanMasterId === null || inspectionPlanMasterId === undefined) {
      dispatch(showToast({
        message: 'Inspection Plan Master ID is required to delete the inspection plan',
        type: 'danger',
      }));
      return;
    }

    // Dispatch the Redux action to delete the inspection plan
    dispatch(deleteInspectionPlanByMasterId(inspectionPlanMasterId));
  };

  const updateFormField = (fieldName, value) => {
    setFormData(prevData => ({
      ...prevData,
      [fieldName]: value,
    }));
    if (onChanges && typeof onChanges === 'function') {
      onChanges({
        ...formData,
        [fieldName]: value,
      });
    }
  };

  const inspectionTypes = [
    { value: 'AQL Inspection', label: 'AQL Inspection' },
    { value: 'Release Inspection', label: 'Release Inspection' },
  ];

  const body = () => {
    return (
      <div className="row">
        {/* <Tabs>
          <Tab title="Safety Equipments/Warnings">
            <SystemRequirement data={safetyData} onChanges={onChangesSafety} />
          </Tab>
          <Tab title="Tools Required">
            <ToolsRequired data={toolsData} onChanges={onChangesTools} />
          </Tab>
          <Tab title="Inspection Plan">
            <InspectionSteps data={stepsData} onChanges={onChangesSteps} />
          </Tab>
        </Tabs> */}
        <Tabs activeIndex={activeTabIndex} onTabChange={index => setActiveTabIndex(index)}>
          <Tab title="Safety Equipments/Warnings">
            <SystemRequirement data={safetyData} onChanges={onChangesSafety} />
          </Tab>
          <Tab title="Tools Required">
            <ToolsRequired data={toolsData} onChanges={onChangesTools} />
          </Tab>
          <Tab title="Inspection Plan">
            <InspectionSteps data={stepsData} onChanges={onChangesSteps} />
          </Tab>
          <Tab title="Parameter">
            <Parameter 
              data={parameterData} 
              onChanges={onChangesParameter} 
              resolvedPartId={resolvedPartId}
              activeParameterId={activeParameterId}
              inspectionPlanId={resolvedInspectionPlanId}
            />

          </Tab>
          <Tab title="Parameters List">
            <ParametersList 
              resolvedPartId={resolvedPartId}
              data={parameterData}
              onEditParameter={handleEditParameter}
              onEditOption={parameterId => handleEditParameter(parameterId)}
              onParametersLoaded={handleParametersLoaded}
              inspectionPlanMasterId={resolvedInspectionPlanId}
            />
          </Tab>
        </Tabs>
      </div>
    );
  };

  const valid = () => {
    if (stepsData?.length === 0) {
      dispatch(
        showToast({
          message: 'Please fill all required fields',
          type: 'danger',
        }),
      );
      return false;
    }
    return true;
  };

  const footer = () => {
    const onSave = () => {
      if (valid()) {
        // Ensure partId is explicitly set and not 0
        const partIdToSave = resolvedPartId || formData.partId || 0;
        
        // Get partNumber from selected part if available
        const selectedPart = partsOptions.find(part => part.value === partIdToSave);
        const partNumberToSave = selectedPart?.label || formData.partNumber || '';
        
        const payload = {
          ...formData,
          // Ensure the backend gets the expected keys with correct values
          partId: partIdToSave,
          partNumber: partNumberToSave,
          inspectionPlanId: formData.inspectionPlanId || formData.id || 0,
          steps: stepsData || [],
          safetyDocuments: safetyData || [],
          toolsDocuments: toolsData || [],
          parameters: parameterData || [],
          savedAt: new Date().toISOString(),
        };
        if (isEditMode) {
          dispatch(editInspectionPlan(payload));
        } else {
          dispatch(addInspectionPlan(payload));
        }
      }
    };

    return (
      <div className="row">
        <div className="col-lg-8">
          <div className="row">
            <div className="col-lg-4">
              <Select
                value={inspectionTypes.find(type => type.value === formData.type)}
                options={inspectionTypes}
                onChange={selected => updateFormField('type', selected.value)}
                className="form-control"
              />
            </div>
            <div className="col-lg-4">
              <Select
                value={partsOptions.find(part => part.value === formData.partId)}
                options={partsOptions}
                onChange={selected => {
                  const partNumber = selected?.label || '';
                  const newPartId = selected?.value || 0;
                  setFormData(prevData => {
                    const updatedData = {
                      ...prevData,
                      partId: newPartId,
                      partNumber: partNumber,
                    };
                    if (onChanges && typeof onChanges === 'function') {
                      onChanges(updatedData);
                    }
                    return updatedData;
                  });
                }}
                className="form-control"
              />
            </div>
            <div className="col-lg-4">
              <div className="d-flex">
              <button type="button" className="btn btn-secondary me-1" onClick={onExtend}>
                Extend
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
              >
                Delete
              </button>

              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4">
        <FooterButton
          cancelText="Cancel"
          saveText="Save Plan"
          onSave={onSave}
          onCancel={handleClose}
        />
      </div>
      </div>
    );
  };

  return (
    <Modal
      id="inspectionplan-modal"
      show={showModal}
      size="xl"
      title={isEditMode ? 'Inspection Plan Details' : 'Add Inspection Plan'}
      body={body}
      footer={footer}
      handleClose={handleClose}
    />
  );
};

InspectionPlanAddEdit.propTypes = {
  showModal: PropTypes.bool,
  modalData: PropTypes.shape(CustomPropTypes.InspectionPlan),
  onCloseModal: PropTypes.func,
  onChanges: PropTypes.func,
};

export default AppModal(InspectionPlanAddEdit);
